
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { KolPic } from "@/types/table";

interface ModifyPasswordDialogProps {
  user: KolPic;
  onPasswordChanged: () => Promise<void>;
}

export function ModifyPasswordDialog({ user, onPasswordChanged }: ModifyPasswordDialogProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const { toast } = useToast();

  const handleUpdatePassword = async () => {
    if (!newPassword.trim()) {
      toast({
        title: "Validation Error",
        description: "Password cannot be empty",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUpdating(true);
      
      // Ensure we have proper authentication context
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to perform this action",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Updating password for user:', user.id);
      console.log('Current auth user:', authUser.id);
      
      const { error } = await supabase
        .from('kol_pic')
        .update({ 
          password: newPassword.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('auth_id', user.auth_id);
      
      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      
      toast({
        title: "Success",
        description: `Password updated for ${user.name}`,
      });
      
      setDialogOpen(false);
      setNewPassword("");
      await onPasswordChanged();
      
    } catch (err: any) {
      console.error("Failed to update password:", err);
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update password. Please check your permissions.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-20 text-xs">
          <KeyRound className="h-3 w-3 mr-1" />
          Pass
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Update password for {user.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleUpdatePassword} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
